/**
 * errorMessages.js — Pre-written user-facing error messages.
 *
 * Maps error types to human-readable titles, messages, and action buttons.
 * These map to the 10 playbooks from Error Brain Part 3 plus common frontend errors.
 * Never exposes raw technical language, stack traces, or error codes.
 */

const ERROR_MESSAGES = {
  DB_TIMEOUT: {
    title: "Having trouble loading",
    message: "We're having a moment — hang tight while we sort it out.",
    action: "Try Again",
  },
  SEARCH_UNAVAILABLE: {
    title: "Search is limited right now",
    message: "Full search is temporarily unavailable. Try browsing by category instead.",
    action: "Browse Categories",
  },
  NOTIFICATION_FAILED: {
    title: "Notification didn't send",
    message: "We couldn't send that notification. Your preferences are saved.",
    action: "Got It",
  },
  EMAIL_FAILED: {
    title: "Email didn't go through",
    message: "We'll try sending that again shortly.",
    action: "OK",
  },
  IMAGE_FAILED: {
    title: "Image unavailable",
    message: "We couldn't load this image right now.",
    action: "Continue",
  },
  SESSION_EXPIRED: {
    title: "You've been signed out",
    message: "Your session expired for security. Sign back in to continue.",
    action: "Sign In",
  },
  NETWORK_SLOW: {
    title: "Connection is slow",
    message: "Things are moving slowly. Check your connection and try again.",
    action: "Retry",
  },
  OFFLINE: {
    title: "You're offline",
    message: "No internet connection detected. We'll sync when you're back online.",
    action: "Got It",
  },
  CLAIM_FAILED: {
    title: "Couldn't save your claim",
    message: "Something went wrong saving this claim. Try again in a moment.",
    action: "Try Again",
  },
  OFFER_LOAD_FAILED: {
    title: "Couldn't load this offer",
    message: "We had trouble loading this deal. It's not gone — try again shortly.",
    action: "Try Again",
  },
  UNKNOWN_ERROR: {
    title: "Something went wrong",
    message: "An unexpected error occurred. Our team has been notified.",
    action: "Try Again",
  },
};

/**
 * Generate a consistent 4-digit error code from a SHA256 fingerprint.
 * Same fingerprint always produces the same code.
 * Mirrors backend logic in error_brain_routes.py.
 */
export function generateErrorCode(fingerprint) {
  if (!fingerprint || fingerprint.length < 8) return "ERR-0000";
  const numeric = parseInt(fingerprint.substring(0, 8), 16) % 10000;
  return `ERR-${String(numeric).padStart(4, "0")}`;
}

/**
 * Look up the error message for a given type.
 * Falls back to UNKNOWN_ERROR if type not found.
 */
export function getErrorMessage(type) {
  return ERROR_MESSAGES[type] || ERROR_MESSAGES.UNKNOWN_ERROR;
}

export default ERROR_MESSAGES;
